import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartIcon from "@mui/icons-material/BarChart";
import { IconButton, CircularProgress } from "@mui/material";
import axios from "../../utils/api.js";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const DaranswerData = () => {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const chartSectionRef = useRef(null);
  const [potentialData, setPotentialData] = useState([
    { name: "High Potential", value: 0 },
    { name: "Medium Potential", value: 0 },
    { name: "Low Potential", value: 0 },
  ]);

  const getPotentialLevel = (totalWeightage) => {
    if (totalWeightage >= 80) return "High Potential";
    if (totalWeightage >= 50) return "Medium Potential";
    return "Low Potential";
  };

  const calculatePotentialLevels = useCallback((data) => {
    let high = 0,
      medium = 0,
      low = 0;

    data.forEach((submission) => {
      const totalWeightage =
        (submission.decisionMakingAuthority || []).reduce(
          (total, item) => total + (item.weightage || 0),
          0
        ) +
        (submission.abilityToPurchase || []).reduce(
          (total, item) => total + (item.weightage || 0),
          0
        ) +
        (submission.readinessToBuy || []).reduce(
          (total, item) => total + (item.weightage || 0),
          0
        );

      const potentialLevel = getPotentialLevel(totalWeightage);

      if (potentialLevel === "High Potential") high++;
      else if (potentialLevel === "Medium Potential") medium++;
      else low++;
    });

    setPotentialData([
      { name: "High Potential", value: high },
      { name: "Medium Potential", value: medium },
      { name: "Low Potential", value: low },
    ]);
  }, []);

  useEffect(() => {
    const fetchAnswers = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Unauthorized: No token found");
        }

        const response = await axios.get("/answers", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 200) {
          setAnswers(response.data);
          calculatePotentialLevels(response.data);
        } else {
          throw new Error("Failed to fetch answers");
        }
      } catch (err) {
        setError(err.message || "No Data Available");
      } finally {
        setLoading(false);
      }
    };

    fetchAnswers();
  }, [calculatePotentialLevels]);

  const COLORS = ["#16a34a", "#eab308", "#dc2626"];

  const scrollToChart = () => {
    chartSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#667eea" }} />
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>Loading...</span>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      {answers.length > 0 && (
        <IconButton
          onClick={scrollToChart}
          title="View Potential Level Distribution chart"
          style={{
            position: "absolute",
            top: 24,
            right: 24,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            color: "#667eea",
            zIndex: 10,
          }}
        >
          <BarChartIcon />
        </IconButton>
      )}

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          DAR Analysis – Potential Level
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View potential level distribution by distributor
        </p>
      </div>

      {error && (
        <p
          style={{
            textAlign: "center",
            color: "#dc2626",
            fontSize: "0.95rem",
            margin: "0 0 20px",
          }}
        >
          {error}
        </p>
      )}

      {answers.length > 0 && (
        <>
          <div
            style={{
              maxWidth: 1000,
              margin: "0 auto 32px",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              backgroundColor: "#fff",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#1e3a5f", color: "#fff" }}>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    S.No
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    Distributor Name
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    Organisation
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    Contact Person
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    Potential Level
                  </th>
                </tr>
              </thead>
              <tbody>
                {answers.map((submission, index) => {
                  const organisation = submission.answers?.[0]?.answer || "N/A";
                  const thirdAnswer = submission.answers?.[3]?.answer || "N/A";
                  const totalWeightage =
                    (submission.decisionMakingAuthority || []).reduce(
                      (total, item) => total + (item.weightage || 0),
                      0
                    ) +
                    (submission.abilityToPurchase || []).reduce(
                      (total, item) => total + (item.weightage || 0),
                      0
                    ) +
                    (submission.readinessToBuy || []).reduce(
                      (total, item) => total + (item.weightage || 0),
                      0
                    );
                  const potentialLevel = getPotentialLevel(totalWeightage);

                  const rowBg =
                    potentialLevel === "High Potential"
                      ? "#f0fdf4"
                      : potentialLevel === "Medium Potential"
                      ? "#fefce8"
                      : "#fef2f2";
                  const levelColor =
                    potentialLevel === "High Potential"
                      ? "#16a34a"
                      : potentialLevel === "Medium Potential"
                      ? "#ca8a04"
                      : "#dc2626";

                  return (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: rowBg,
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px 16px",
                          fontSize: 14,
                          color: "#334155",
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          fontSize: 14,
                          color: "#334155",
                        }}
                      >
                        {submission.distributorName}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          fontSize: 14,
                          color: "#334155",
                        }}
                      >
                        {organisation}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          fontSize: 14,
                          color: "#334155",
                        }}
                      >
                        {thirdAnswer}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          fontSize: 14,
                          fontWeight: 600,
                          color: levelColor,
                        }}
                      >
                        {potentialLevel}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div
            ref={chartSectionRef}
            style={{
              maxWidth: 720,
              margin: "0 auto",
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            }}
          >
            <h3
              style={{
                margin: "0 0 20px",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#1e293b",
                textAlign: "center",
              }}
            >
              Potential Level Distribution
            </h3>
            <PieChart width={660} height={400}>
              <Pie
                data={potentialData}
                cx="50%"
                cy="50%"
                outerRadius={140}
                fill="#8884d8"
                dataKey="value"
                label
              >
                {potentialData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </div>
        </>
      )}
    </div>
  );
};

export default DaranswerData;
